import { APPRECIATION_INDEX, TESTIMONIALS, GOOGLE_REVIEWS_URL } from "@/constants/testIds";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

export default function ProofOfPeace() {
  return (
    <div>
      <section className="relative min-h-[55vh] bg-forest overflow-hidden">
        <div className="absolute inset-0 bg-grain" />
        <div className="relative container-editorial px-5 md:px-10 pt-32 pb-16 md:pb-20 text-cream">
          <div className="max-w-3xl fade-up">
            <div className="eyebrow text-lime mb-4">
              <span className="editorial-rule bg-lime" /> Proof of Peace
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.98]">
              Two phases,<br />two waiting lists,<br />no advertising.
            </h1>
            <p className="mt-6 text-cream/85 max-w-xl leading-relaxed">
              Phase I and Phase II of Pali Hill City are sold out. Chapter III
              is the last phase of the layout. What you're about to read is not
              a pitch — it is what the last set of buyers said out loud.
            </p>
          </div>
        </div>
      </section>

      {/* Phase status */}
      <section className="section-pad bg-offwhite">
        <div className="container-editorial grid md:grid-cols-3 gap-6">
          {[
            { title: "Phase I", status: "Sold out", note: "The founding cohort. Weekend homes built and lived in." },
            { title: "Phase II", status: "Sold out", note: "The second cohort. Golden-hour visits closed the phase." },
            { title: "Chapter III", status: "Opens Sept 12–13, 2026", note: "The final phase of the 300-acre layout." },
          ].map((p, i) => (
            <div
              key={p.title}
              className={`p-8 rounded-lg border transition-colors ${
                i === 2
                  ? "border-forest bg-forest text-cream"
                  : "border-forest/15 bg-white text-forest"
              }`}
            >
              <div className={`eyebrow ${i === 2 ? "text-lime" : "text-teal"}`}>{p.title}</div>
              <div className="font-display text-3xl md:text-4xl mt-3 leading-tight">{p.status}</div>
              <p className={`mt-4 text-sm leading-relaxed ${i === 2 ? "text-cream/80" : "text-forest/70"}`}>
                {p.note}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Appreciation chart */}
      <section className="section-pad bg-cream">
        <div className="container-editorial grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow text-teal mb-4"><span className="editorial-rule" /> The Index</div>
            <h2 className="font-display text-forest text-3xl md:text-4xl leading-tight">
              Land has done what land does.
            </h2>
            <p className="mt-6 text-forest/70 leading-relaxed max-w-md">
              The layout&apos;s appreciation index, indexed to 100 at 2019. This is
              a general trend for the layout, not a promise of returns and not
              a per-plot figure. Detailed per-sqft data is available on request
              at the site office.
            </p>
          </div>
          <div className="md:col-span-8 bg-white border border-forest/10 rounded-lg p-4 md:p-6 h-[380px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={APPRECIATION_INDEX} margin={{ top: 20, right: 20, left: 0, bottom: 10 }}>
                <CartesianGrid stroke="#0B4A3D" strokeOpacity={0.08} vertical={false} />
                <XAxis dataKey="year" stroke="#0B4A3D" strokeOpacity={0.5} tick={{ fill: "#0B4A3D", fontSize: 12 }} />
                <YAxis stroke="#0B4A3D" strokeOpacity={0.5} tick={{ fill: "#0B4A3D", fontSize: 12 }} domain={[80, 360]} />
                <Tooltip
                  contentStyle={{ background: "#0B4A3D", border: "none", color: "#F4F4EF", borderRadius: 8 }}
                  labelStyle={{ color: "#A6CE39", fontWeight: 600 }}
                  formatter={(value) => [`Index ${value}`, "Layout"]}
                />
                <Line type="monotone" dataKey="value" stroke="#0B4A3D" strokeWidth={2.5} dot={{ r: 4, fill: "#A6CE39", stroke: "#0B4A3D" }} activeDot={{ r: 6, fill: "#A6CE39" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-pad bg-offwhite">
        <div className="container-editorial">
          <div className="eyebrow text-teal mb-6"><span className="editorial-rule" /> Google reviews · Trust & Track Record</div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 max-w-4xl">
            <h2 className="font-display text-forest text-3xl md:text-5xl leading-tight max-w-2xl">
              Hear what our past<br />customers have to say.
            </h2>
            <a
              href={GOOGLE_REVIEWS_URL}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-2 text-teal hover:text-forest text-sm"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Read all reviews on Google &rarr;
            </a>
          </div>
          <p className="mt-4 text-forest/70 max-w-2xl text-[15px] leading-relaxed">
            Verified reviews from plot owners at Pali Hill City and other
            layouts developed by our team &mdash; pulled from public Google
            Maps and Housing.com listings. The same team, the same paperwork
            discipline, now bringing Chapter III to Pali.
          </p>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <article
                key={i}
                className="bg-white border border-forest/10 rounded-2xl p-5 md:p-6 shadow-[0_20px_60px_-40px_rgba(11,74,61,0.4)] flex flex-col"
              >
                <header className="flex items-center gap-3 pb-3 border-b border-forest/10">
                  <div className="w-10 h-10 rounded-full grid place-items-center bg-forest text-cream font-display text-sm">
                    {t.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                  </div>
                  <div className="min-w-0">
                    <div className="text-forest text-sm font-semibold leading-tight truncate">{t.name}</div>
                    <div className="text-forest/50 text-[11px] flex items-center gap-1.5">
                      <svg width="12" height="12" viewBox="0 0 24 24" aria-label="Google">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      {t.when}
                    </div>
                  </div>
                </header>
                <div className="flex items-center gap-1 mt-3">
                  {Array.from({ length: 5 }).map((_, s) => (
                    <svg key={s} width="14" height="14" viewBox="0 0 24 24" fill={s < (t.rating || 5) ? "#FBBC05" : "#e5e7eb"} aria-hidden="true">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                    </svg>
                  ))}
                </div>
                <p className="mt-3 text-forest/80 text-sm leading-relaxed flex-1">{t.quote}</p>
                <footer className="mt-4 pt-3 border-t border-forest/10 text-[11px] text-forest/55">{t.role}</footer>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-forest text-cream">
        <div className="container-editorial flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <h2 className="font-display text-4xl md:text-5xl leading-tight max-w-xl">
            A free online consultation with our sales team — added straight to your Google Calendar.
          </h2>
          <Link to="/strategy-call" className="btn-primary">Book my consultation <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}
